import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { 
  HelpCircle, Fish, Layers, Circle, FileText, ChevronRight, 
  AlertTriangle, Users, Cog, Lightbulb, Plus, X, Wrench,
  Ruler, Leaf, CircleDot
} from "lucide-react";

export type RCAMethod = "5_whys" | "fishbone" | "icam" | "swiss_cheese" | "free_form";

export interface RCAData {
  method: RCAMethod;
  // 5 Whys
  whys?: string[];
  rootCause?: string;
  // Fishbone
  fishbone?: {
    man?: string[];
    machine?: string[];
    method?: string[];
    material?: string[];
    measurement?: string[];
    environment?: string[];
    notes?: string;
  };
  // ICAM
  icam?: {
    absentDefenses?: string[];
    individualActions?: { errors: string[]; violations: string[] };
    taskConditions?: string[];
    organizationalFactors?: string[];
    notes?: string;
  };
  // Swiss Cheese
  swissCheese?: {
    layers: {
      name: string;
      holes: string[];
      failureType: "latent" | "active";
    }[];
    notes?: string;
  };
  // Free Form
  freeForm?: string;
}

interface InvestigationMethodsProps {
  selectedMethod: RCAMethod;
  onMethodChange: (method: RCAMethod) => void;
  data: RCAData;
  onDataChange: (data: RCAData) => void;
  learningStatement: string;
  onLearningStatementChange: (value: string) => void;
  readonly?: boolean;
}

const METHOD_INFO: Record<RCAMethod, { label: string; icon: React.ReactNode; description: string }> = {
  "5_whys": { 
    label: "5 Whys", 
    icon: <HelpCircle className="h-4 w-4" />,
    description: "Tanyakan 'mengapa' berulang untuk menemukan akar masalah"
  },
  fishbone: { 
    label: "Fishbone", 
    icon: <Fish className="h-4 w-4" />,
    description: "Diagram Ishikawa - 6M Analysis"
  },
  icam: { 
    label: "ICAM", 
    icon: <Layers className="h-4 w-4" />,
    description: "Incident Cause Analysis Method"
  },
  swiss_cheese: { 
    label: "Swiss Cheese", 
    icon: <Circle className="h-4 w-4" />,
    description: "Model Reason - lapisan pertahanan"
  },
  free_form: { 
    label: "Free Form", 
    icon: <FileText className="h-4 w-4" />,
    description: "Naratif bebas"
  },
};

// ============ 5 WHYS COMPONENT ============
function FiveWhysMethod({ data, onChange, readonly }: { 
  data: RCAData; 
  onChange: (data: RCAData) => void;
  readonly?: boolean;
}) {
  const whys = data.whys || ["", "", "", "", ""];
  const rootCause = data.rootCause || "";

  const updateWhy = (index: number, value: string) => {
    const newWhys = [...whys];
    newWhys[index] = value;
    onChange({ ...data, whys: newWhys });
  };

  return (
    <div className="space-y-4">
      <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
        <p className="text-sm text-muted-foreground">
          <strong>Metode 5 Whys:</strong> Tanyakan "mengapa" secara berulang untuk menggali 
          hingga akar masalah yang sebenarnya. Setiap jawaban menjadi dasar pertanyaan berikutnya.
        </p>
      </div>

      <div className="relative pl-6">
        {/* Vertical line connector */}
        <div className="absolute left-[11px] top-4 bottom-4 w-0.5 bg-gradient-to-b from-primary via-primary/50 to-primary/20" />
        
        {whys.map((why, idx) => (
          <div key={idx} className="relative pb-6 last:pb-0">
            {/* Circle node */}
            <div className={cn(
              "absolute -left-6 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold z-10",
              why ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground border-2 border-dashed"
            )}>
              {idx + 1}
            </div>
            
            <div className="ml-4 space-y-2">
              <Label className="font-medium text-primary">
                Mengapa {idx + 1}
                {idx > 0 && whys[idx - 1] && (
                  <span className="font-normal text-muted-foreground ml-2">
                    (karena: "{whys[idx - 1].slice(0, 40)}...")
                  </span>
                )}
              </Label>
              <Textarea
                placeholder={idx === 0 ? "Mengapa insiden ini terjadi?" : `Mengapa hal tersebut terjadi?`}
                value={why}
                onChange={(e) => updateWhy(idx, e.target.value)}
                disabled={readonly || (idx > 0 && !whys[idx - 1])}
                className={cn(
                  "min-h-[60px] transition-all",
                  idx > 0 && !whys[idx - 1] && "opacity-50"
                )}
              />
            </div>
          </div>
        ))}
      </div>
      
      <div className="pt-4 border-t-2 border-primary space-y-2 mt-6">
        <Label className="font-semibold text-lg flex items-center gap-2 text-primary">
          <Lightbulb className="h-5 w-5" />
          Akar Masalah (Root Cause)
        </Label>
        <Textarea
          placeholder="Ringkasan akar masalah yang teridentifikasi dari analisis 5 Whys..."
          value={rootCause}
          onChange={(e) => onChange({ ...data, rootCause: e.target.value })}
          disabled={readonly}
          className="min-h-[100px] border-primary/50 focus:border-primary"
        />
      </div>
    </div>
  );
}

// ============ FISHBONE (ISHIKAWA) COMPONENT ============
const FISHBONE_CATEGORIES = {
  man: { label: "Man", subtitle: "Manusia", icon: <Users className="h-4 w-4" />, color: "text-blue-600" },
  method: { label: "Method", subtitle: "Metode", icon: <FileText className="h-4 w-4" />, color: "text-green-600" },
  machine: { label: "Machine", subtitle: "Mesin", icon: <Cog className="h-4 w-4" />, color: "text-orange-600" },
  material: { label: "Material", subtitle: "Material", icon: <Wrench className="h-4 w-4" />, color: "text-purple-600" },
  measurement: { label: "Measurement", subtitle: "Pengukuran", icon: <Ruler className="h-4 w-4" />, color: "text-amber-600" },
  environment: { label: "Environment", subtitle: "Lingkungan", icon: <Leaf className="h-4 w-4" />, color: "text-teal-600" },
};

function FishboneMethod({ data, onChange, readonly }: { 
  data: RCAData; 
  onChange: (data: RCAData) => void;
  readonly?: boolean;
}) {
  const fishbone = data.fishbone || {};
  const [inputValues, setInputValues] = useState<Record<string, string>>({});

  const addItem = (category: keyof typeof FISHBONE_CATEGORIES) => {
    const value = inputValues[category]?.trim();
    if (!value) return;
    
    const current = fishbone[category] || [];
    if (!current.includes(value)) {
      onChange({ 
        ...data, 
        fishbone: { ...fishbone, [category]: [...current, value] } 
      });
    }
    setInputValues({ ...inputValues, [category]: "" });
  };

  const removeItem = (category: keyof typeof FISHBONE_CATEGORIES, item: string) => {
    const current = fishbone[category] || [];
    onChange({ 
      ...data, 
      fishbone: { ...fishbone, [category]: current.filter(i => i !== item) } 
    });
  };

  const topCategories: (keyof typeof FISHBONE_CATEGORIES)[] = ["man", "method", "machine"];
  const bottomCategories: (keyof typeof FISHBONE_CATEGORIES)[] = ["material", "measurement", "environment"];

  const renderBone = (key: keyof typeof FISHBONE_CATEGORIES, position: "top" | "bottom") => {
    const category = FISHBONE_CATEGORIES[key];
    const items = fishbone[key] || [];
    
    return (
      <div key={key} className={cn(
        "relative flex flex-col",
        position === "top" ? "items-end" : "items-end"
      )}>
        {/* Bone header */}
        <div className={cn(
          "flex items-center gap-2 px-3 py-1.5 rounded-lg border-2 mb-2",
          category.color,
          "bg-background border-current"
        )}>
          {category.icon}
          <div>
            <span className="font-bold text-sm">{category.label}</span>
            <span className="text-xs text-muted-foreground ml-1">({category.subtitle})</span>
          </div>
        </div>
        
        {/* Diagonal line effect */}
        <div className={cn(
          "absolute w-24 h-0.5 bg-muted-foreground/30",
          position === "top" ? "bottom-0 -rotate-45 origin-bottom-right right-0" : "top-0 rotate-45 origin-top-right right-0"
        )} />
        
        {/* Items (causes) */}
        <div className="space-y-1 max-w-[200px]">
          {items.map((item, idx) => (
            <div 
              key={idx} 
              className="flex items-center gap-1 group"
            >
              <Badge 
                variant="secondary" 
                className={cn(
                  "text-xs cursor-pointer hover:bg-destructive hover:text-destructive-foreground transition-colors",
                  readonly && "pointer-events-none"
                )}
                onClick={() => !readonly && removeItem(key, item)}
              >
                {item}
                {!readonly && <X className="h-3 w-3 ml-1 opacity-0 group-hover:opacity-100" />}
              </Badge>
            </div>
          ))}
          
          {/* Add input */}
          {!readonly && (
            <div className="flex gap-1 mt-2">
              <Input
                placeholder="+ Tambah penyebab"
                value={inputValues[key] || ""}
                onChange={(e) => setInputValues({ ...inputValues, [key]: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && addItem(key)}
                className="h-7 text-xs"
              />
              <Button 
                size="sm" 
                variant="ghost" 
                className="h-7 w-7 p-0"
                onClick={() => addItem(key)}
              >
                <Plus className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
        <p className="text-sm text-muted-foreground">
          <strong>Diagram Fishbone (Ishikawa):</strong> Identifikasi penyebab dari 6 kategori utama (6M). 
          Tambahkan penyebab di setiap kategori yang relevan dengan insiden.
        </p>
      </div>

      {/* Fishbone Diagram */}
      <div className="relative p-6 border rounded-xl bg-gradient-to-r from-muted/30 via-background to-muted/50 overflow-hidden">
        {/* Top bones */}
        <div className="flex justify-around mb-8 relative z-10">
          {topCategories.map((key) => renderBone(key, "top"))}
        </div>
        
        {/* Main spine */}
        <div className="relative flex items-center justify-center py-4">
          <div className="absolute left-0 right-20 h-1 bg-gradient-to-r from-muted-foreground/20 via-muted-foreground to-primary rounded-full" />
          
          {/* Fish head (Problem) */}
          <div className="absolute right-0 flex items-center">
            <div className="w-0 h-0 border-t-[20px] border-b-[20px] border-l-[15px] border-t-transparent border-b-transparent border-l-primary" />
            <div className="bg-primary text-primary-foreground px-4 py-2 rounded-r-full font-bold text-sm">
              PROBLEM
            </div>
          </div>
        </div>
        
        {/* Bottom bones */}
        <div className="flex justify-around mt-8 relative z-10">
          {bottomCategories.map((key) => renderBone(key, "bottom"))}
        </div>
      </div>
      
      {/* Notes */}
      <div className="space-y-2">
        <Label>Catatan Tambahan</Label>
        <Textarea
          placeholder="Catatan atau analisis tambahan..."
          value={fishbone.notes || ""}
          onChange={(e) => onChange({ ...data, fishbone: { ...fishbone, notes: e.target.value } })}
          disabled={readonly}
          className="min-h-[80px]"
        />
      </div>
    </div>
  );
}

// ============ SWISS CHEESE MODEL COMPONENT ============
const DEFAULT_LAYERS = [
  { name: "Organizational Influences", nameId: "Pengaruh Organisasi", description: "Budaya, kebijakan, sumber daya" },
  { name: "Unsafe Supervision", nameId: "Pengawasan Tidak Aman", description: "Pengawasan, leadership, perencanaan" },
  { name: "Preconditions", nameId: "Prakondisi", description: "Kondisi individu, lingkungan, alat" },
  { name: "Unsafe Acts", nameId: "Tindakan Tidak Aman", description: "Errors, violations, shortcuts" },
];

function SwissCheeseMethod({ data, onChange, readonly }: { 
  data: RCAData; 
  onChange: (data: RCAData) => void;
  readonly?: boolean;
}) {
  const swissCheese = data.swissCheese || {
    layers: DEFAULT_LAYERS.map(l => ({ name: l.name, holes: [] as string[], failureType: "latent" as const })),
    notes: "",
  };
  const [inputValues, setInputValues] = useState<Record<number, string>>({});

  const addHole = (layerIdx: number) => {
    const value = inputValues[layerIdx]?.trim();
    if (!value) return;
    
    const layers = [...swissCheese.layers];
    if (!layers[layerIdx].holes.includes(value)) {
      layers[layerIdx].holes = [...layers[layerIdx].holes, value];
      onChange({ ...data, swissCheese: { ...swissCheese, layers } });
    }
    setInputValues({ ...inputValues, [layerIdx]: "" });
  };

  const removeHole = (layerIdx: number, hole: string) => {
    const layers = [...swissCheese.layers];
    layers[layerIdx].holes = layers[layerIdx].holes.filter(h => h !== hole);
    onChange({ ...data, swissCheese: { ...swissCheese, layers } });
  };

  // Check if holes are "aligned" (at least one hole per layer)
  const holesAligned = swissCheese.layers.every(l => l.holes.length > 0);

  return (
    <div className="space-y-4">
      <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
        <p className="text-sm text-muted-foreground">
          <strong>Model Swiss Cheese (James Reason):</strong> Setiap lapisan pertahanan memiliki "lubang" (kelemahan). 
          Insiden terjadi ketika lubang-lubang sejajar. Identifikasi kelemahan di setiap lapisan.
        </p>
      </div>

      {/* Visual Swiss Cheese */}
      <div className="relative space-y-4 p-4">
        {/* Alignment indicator */}
        {holesAligned && (
          <div className="absolute left-1/2 top-0 bottom-16 w-1 bg-gradient-to-b from-destructive via-destructive to-transparent opacity-50 z-0 -translate-x-1/2" />
        )}
        
        {swissCheese.layers.map((layer, idx) => {
          const layerInfo = DEFAULT_LAYERS[idx];
          const hasHoles = layer.holes.length > 0;
          
          return (
            <div key={idx} className="relative">
              {/* Cheese slice */}
              <div className={cn(
                "relative rounded-2xl p-4 border-2 transition-all",
                hasHoles 
                  ? "bg-amber-100/50 border-amber-300 dark:bg-amber-900/20" 
                  : "bg-green-100/50 border-green-300 dark:bg-green-900/20"
              )}>
                {/* Wavy pattern overlay for cheese effect */}
                <div 
                  className="absolute inset-0 opacity-10 rounded-2xl"
                  style={{
                    backgroundImage: `radial-gradient(circle at 20% 50%, transparent 30%, currentColor 31%, currentColor 32%, transparent 33%),
                                      radial-gradient(circle at 60% 30%, transparent 20%, currentColor 21%, currentColor 22%, transparent 23%),
                                      radial-gradient(circle at 80% 70%, transparent 25%, currentColor 26%, currentColor 27%, transparent 28%)`,
                    backgroundSize: "100px 100px"
                  }}
                />
                
                <div className="relative z-10">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-semibold flex items-center gap-2">
                        <span className={cn(
                          "w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold",
                          hasHoles ? "bg-amber-500 text-white" : "bg-green-500 text-white"
                        )}>
                          {idx + 1}
                        </span>
                        {layerInfo?.nameId || layer.name}
                      </h4>
                      <p className="text-xs text-muted-foreground ml-8">{layerInfo?.description}</p>
                    </div>
                    <Badge variant={hasHoles ? "destructive" : "secondary"}>
                      {layer.holes.length} lubang
                    </Badge>
                  </div>
                  
                  {/* Holes visualization */}
                  <div className="flex flex-wrap gap-2 ml-8">
                    {layer.holes.map((hole, holeIdx) => (
                      <div
                        key={holeIdx}
                        className={cn(
                          "relative group cursor-pointer",
                          readonly && "pointer-events-none"
                        )}
                        onClick={() => !readonly && removeHole(idx, hole)}
                      >
                        {/* Hole circle */}
                        <div className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-amber-500/20 border-2 border-dashed border-amber-500 hover:bg-destructive/20 hover:border-destructive transition-colors">
                          <CircleDot className="h-3 w-3 text-amber-600" />
                          <span className="text-xs font-medium">{hole}</span>
                          {!readonly && (
                            <X className="h-3 w-3 opacity-0 group-hover:opacity-100 text-destructive" />
                          )}
                        </div>
                      </div>
                    ))}
                    
                    {/* Add hole input */}
                    {!readonly && (
                      <div className="flex items-center gap-1">
                        <Input
                          placeholder="+ Lubang baru..."
                          value={inputValues[idx] || ""}
                          onChange={(e) => setInputValues({ ...inputValues, [idx]: e.target.value })}
                          onKeyDown={(e) => e.key === "Enter" && addHole(idx)}
                          className="h-8 w-40 text-xs rounded-full"
                        />
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="h-8 w-8 p-0 rounded-full"
                          onClick={() => addHole(idx)}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Arrow between layers */}
              {idx < swissCheese.layers.length - 1 && (
                <div className="flex justify-center py-1">
                  <ChevronRight className="h-5 w-5 text-muted-foreground rotate-90" />
                </div>
              )}
            </div>
          );
        })}
        
        {/* Incident result */}
        <div className={cn(
          "text-center py-4 rounded-xl border-2 transition-all",
          holesAligned 
            ? "bg-destructive/10 border-destructive" 
            : "bg-muted border-muted-foreground/20"
        )}>
          <AlertTriangle className={cn(
            "h-8 w-8 mx-auto mb-2",
            holesAligned ? "text-destructive" : "text-muted-foreground"
          )} />
          <p className={cn(
            "font-bold",
            holesAligned ? "text-destructive" : "text-muted-foreground"
          )}>
            {holesAligned ? "⚠️ INCIDENT TERJADI" : "Insiden Terhalang"}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {holesAligned 
              ? "Lubang pada semua lapisan sejajar" 
              : "Tambahkan lubang di setiap lapisan untuk melihat bagaimana insiden terjadi"}
          </p>
        </div>
      </div>
      
      {/* Notes */}
      <div className="space-y-2">
        <Label>Analisis Swiss Cheese</Label>
        <Textarea
          placeholder="Bagaimana lubang-lubang ini sejajar menyebabkan insiden terjadi..."
          value={swissCheese.notes || ""}
          onChange={(e) => onChange({ ...data, swissCheese: { ...swissCheese, notes: e.target.value } })}
          disabled={readonly}
          className="min-h-[80px]"
        />
      </div>
    </div>
  );
}

// ============ ICAM COMPONENT ============
function ICAMMethod({ data, onChange, readonly }: { 
  data: RCAData; 
  onChange: (data: RCAData) => void;
  readonly?: boolean;
}) {
  const icam = data.icam || { 
    absentDefenses: [], 
    individualActions: { errors: [], violations: [] },
    taskConditions: [],
    organizationalFactors: [],
  };
  const [inputValues, setInputValues] = useState<Record<string, string>>({});

  const addItem = (category: string) => {
    const value = inputValues[category]?.trim();
    if (!value) return;
    
    if (category === "errors" || category === "violations") {
      const actions = icam.individualActions || { errors: [], violations: [] };
      const current = actions[category] || [];
      if (!current.includes(value)) {
        onChange({ 
          ...data, 
          icam: { 
            ...icam, 
            individualActions: { ...actions, [category]: [...current, value] }
          } 
        });
      }
    } else {
      const current = (icam as any)[category] || [];
      if (!current.includes(value)) {
        onChange({ ...data, icam: { ...icam, [category]: [...current, value] } });
      }
    }
    setInputValues({ ...inputValues, [category]: "" });
  };

  const removeItem = (category: string, item: string) => {
    if (category === "errors" || category === "violations") {
      const actions = icam.individualActions || { errors: [], violations: [] };
      const current = actions[category] || [];
      onChange({ 
        ...data, 
        icam: { 
          ...icam, 
          individualActions: { ...actions, [category]: current.filter(i => i !== item) }
        } 
      });
    } else {
      const current = (icam as any)[category] || [];
      onChange({ ...data, icam: { ...icam, [category]: current.filter((i: string) => i !== item) } });
    }
  };

  const getItems = (category: string): string[] => {
    if (category === "errors" || category === "violations") {
      return icam.individualActions?.[category] || [];
    }
    return (icam as any)[category] || [];
  };

  const renderSection = (key: string, title: string, description: string, color: string) => {
    const items = getItems(key);
    
    return (
      <Card className={cn("border-l-4", color)}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">{title}</CardTitle>
          <CardDescription className="text-xs">{description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex flex-wrap gap-1">
            {items.map((item, idx) => (
              <Badge 
                key={idx}
                variant="secondary"
                className={cn(
                  "cursor-pointer hover:bg-destructive hover:text-destructive-foreground transition-colors",
                  readonly && "pointer-events-none"
                )}
                onClick={() => !readonly && removeItem(key, item)}
              >
                {item}
                {!readonly && <X className="h-3 w-3 ml-1" />}
              </Badge>
            ))}
          </div>
          {!readonly && (
            <div className="flex gap-1">
              <Input
                placeholder="+ Tambah..."
                value={inputValues[key] || ""}
                onChange={(e) => setInputValues({ ...inputValues, [key]: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && addItem(key)}
                className="h-7 text-xs"
              />
              <Button 
                size="sm" 
                variant="ghost" 
                className="h-7 w-7 p-0"
                onClick={() => addItem(key)}
              >
                <Plus className="h-3 w-3" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-4">
      <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
        <p className="text-sm text-muted-foreground">
          <strong>ICAM (Incident Cause Analysis Method):</strong> Analisis sistematis dari pertahanan yang gagal, 
          tindakan individu, kondisi tugas, dan faktor organisasi yang berkontribusi pada insiden.
        </p>
      </div>

      {/* ICAM Flow Diagram */}
      <div className="space-y-4">
        {/* Top row - 3 columns */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {renderSection(
            "absentDefenses", 
            "🛡️ Absent/Failed Defenses", 
            "Pertahanan yang tidak ada atau gagal",
            "border-l-red-500"
          )}
          
          {/* Individual Actions - Center */}
          <Card className="border-l-4 border-l-orange-500">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">👤 Individual Actions</CardTitle>
              <CardDescription className="text-xs">Tindakan individu yang berkontribusi</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs text-muted-foreground">Errors (Kesalahan)</Label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {getItems("errors").map((item, idx) => (
                    <Badge 
                      key={idx}
                      variant="secondary"
                      className={cn(
                        "cursor-pointer hover:bg-destructive hover:text-destructive-foreground text-xs",
                        readonly && "pointer-events-none"
                      )}
                      onClick={() => !readonly && removeItem("errors", item)}
                    >
                      {item}
                      {!readonly && <X className="h-2 w-2 ml-1" />}
                    </Badge>
                  ))}
                </div>
                {!readonly && (
                  <div className="flex gap-1 mt-1">
                    <Input
                      placeholder="+ Error..."
                      value={inputValues.errors || ""}
                      onChange={(e) => setInputValues({ ...inputValues, errors: e.target.value })}
                      onKeyDown={(e) => e.key === "Enter" && addItem("errors")}
                      className="h-6 text-xs"
                    />
                  </div>
                )}
              </div>
              
              <div>
                <Label className="text-xs text-muted-foreground">Violations (Pelanggaran)</Label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {getItems("violations").map((item, idx) => (
                    <Badge 
                      key={idx}
                      variant="secondary"
                      className={cn(
                        "cursor-pointer hover:bg-destructive hover:text-destructive-foreground text-xs",
                        readonly && "pointer-events-none"
                      )}
                      onClick={() => !readonly && removeItem("violations", item)}
                    >
                      {item}
                      {!readonly && <X className="h-2 w-2 ml-1" />}
                    </Badge>
                  ))}
                </div>
                {!readonly && (
                  <div className="flex gap-1 mt-1">
                    <Input
                      placeholder="+ Violation..."
                      value={inputValues.violations || ""}
                      onChange={(e) => setInputValues({ ...inputValues, violations: e.target.value })}
                      onKeyDown={(e) => e.key === "Enter" && addItem("violations")}
                      className="h-6 text-xs"
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          
          {renderSection(
            "taskConditions", 
            "🌍 Task/Environmental Conditions", 
            "Kondisi tugas dan lingkungan",
            "border-l-blue-500"
          )}
        </div>
        
        {/* Flow arrows */}
        <div className="flex justify-center gap-4">
          <ChevronRight className="h-6 w-6 text-muted-foreground rotate-90" />
        </div>
        
        {/* Organizational Factors */}
        <div className="max-w-md mx-auto">
          {renderSection(
            "organizationalFactors", 
            "🏢 Organizational Factors", 
            "Faktor organisasi yang berkontribusi",
            "border-l-purple-500"
          )}
        </div>
        
        {/* Flow to incident */}
        <div className="flex justify-center">
          <ChevronRight className="h-6 w-6 text-muted-foreground rotate-90" />
        </div>
        
        {/* Incident */}
        <div className="text-center py-3 rounded-xl bg-destructive/10 border-2 border-destructive">
          <AlertTriangle className="h-6 w-6 mx-auto mb-1 text-destructive" />
          <p className="font-bold text-destructive">INCIDENT</p>
        </div>
      </div>
      
      {/* Notes */}
      <div className="space-y-2">
        <Label>Ringkasan Analisis ICAM</Label>
        <Textarea
          placeholder="Ringkasan bagaimana faktor-faktor di atas berkontribusi pada insiden..."
          value={icam.notes || ""}
          onChange={(e) => onChange({ ...data, icam: { ...icam, notes: e.target.value } })}
          disabled={readonly}
          className="min-h-[80px]"
        />
      </div>
    </div>
  );
}

// ============ FREE FORM COMPONENT ============
function FreeFormMethod({ data, onChange, readonly }: { 
  data: RCAData; 
  onChange: (data: RCAData) => void;
  readonly?: boolean;
}) {
  return (
    <div className="space-y-4">
      <div className="p-4 bg-muted/50 rounded-lg">
        <p className="text-sm text-muted-foreground">
          Gunakan format bebas untuk menjelaskan penyebab insiden, faktor kontributor, 
          dan pembelajaran yang didapat. Cocok untuk insiden sederhana.
        </p>
      </div>
      
      <Textarea
        placeholder="Jelaskan analisis investigasi Anda di sini..."
        value={data.freeForm || ""}
        onChange={(e) => onChange({ ...data, freeForm: e.target.value })}
        disabled={readonly}
        className="min-h-[250px]"
      />
    </div>
  );
}

// ============ MAIN COMPONENT ============
export function InvestigationMethods({
  selectedMethod,
  onMethodChange,
  data,
  onDataChange,
  learningStatement,
  onLearningStatementChange,
  readonly = false,
}: InvestigationMethodsProps) {
  return (
    <div className="space-y-6">
      {/* Method Selector */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">Pilih Metode Investigasi</Label>
        <div className="flex flex-wrap gap-2">
          {(Object.entries(METHOD_INFO) as [RCAMethod, typeof METHOD_INFO["5_whys"]][]).map(([key, info]) => (
            <Button
              key={key}
              variant={selectedMethod === key ? "default" : "outline"}
              size="sm"
              onClick={() => !readonly && onMethodChange(key)}
              disabled={readonly}
              className="gap-2"
            >
              {info.icon}
              {info.label}
            </Button>
          ))}
        </div>
        <p className="text-sm text-muted-foreground">
          {METHOD_INFO[selectedMethod].description}
        </p>
      </div>

      {/* Method Content */}
      <Card>
        <CardContent className="pt-6">
          {selectedMethod === "5_whys" && (
            <FiveWhysMethod data={data} onChange={onDataChange} readonly={readonly} />
          )}
          {selectedMethod === "fishbone" && (
            <FishboneMethod data={data} onChange={onDataChange} readonly={readonly} />
          )}
          {selectedMethod === "icam" && (
            <ICAMMethod data={data} onChange={onDataChange} readonly={readonly} />
          )}
          {selectedMethod === "swiss_cheese" && (
            <SwissCheeseMethod data={data} onChange={onDataChange} readonly={readonly} />
          )}
          {selectedMethod === "free_form" && (
            <FreeFormMethod data={data} onChange={onDataChange} readonly={readonly} />
          )}
        </CardContent>
      </Card>

      {/* Learning Statement */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-primary" />
            Learning Statement
          </CardTitle>
          <CardDescription>
            Pernyataan pembelajaran yang dapat dibagikan ke seluruh pekerja
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            placeholder="Tuliskan pernyataan pembelajaran dari insiden ini yang dapat dipahami oleh semua pekerja..."
            value={learningStatement}
            onChange={(e) => onLearningStatementChange(e.target.value)}
            disabled={readonly}
            className="min-h-[100px]"
          />
          <p className="text-xs text-muted-foreground mt-2">
            💡 Statement ini akan di-rewrite oleh AI untuk bahasa yang lebih jelas dan actionable
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
